import { useController } from 'react-hook-form'
import styled, { css } from 'styled-components'

import { Checkbox } from '@components/ui/Checkbox'
import { FormControlLabel } from '@components/ui/FormElements/FormControlLabel'

import { FormControl } from './FormControl'
import { FormHelperText } from './FormHelperText'

const StyledCheckbox = styled(Checkbox)`
  &.MuiCheckbox-colorPrimary.Mui-disabled {
    opacity: 0.3;
  }
`

const StyledFormControlLabel = styled(FormControlLabel)<{
  $isLabelHighlighted?: boolean
}>`
  align-items: flex-start;
  padding-top: 6px;
  margin-bottom: 5px;
  width: max-content;
  max-width: 100%;

  &[data-disabled='true'] {
    & > *:first-child {
      opacity: 0.3;
    }
    & > span:last-child {
      color: ${({ theme }) => theme.colors.gray[100]};
    }
  }

  ${({ $isLabelHighlighted }) =>
    $isLabelHighlighted &&
    css`
      & > span:last-child {
        font-weight: 700;
        color: ${({ theme }) => theme.colors.gray[100]};
      }
    `}
`

interface CheckboxProps {
  className?: string
  disabled?: boolean
  isLabelHighlighted?: boolean
  label: StringOrMessage
  name: string
}

export function ReactHookFormCheckboxField({
  className,
  disabled = false,
  isLabelHighlighted = false,
  label,
  name,
}: CheckboxProps) {
  const {
    field,
    fieldState: { error },
    formState: { isSubmitting },
  } = useController({ name })

  const hasError = !!error
  const isFieldDisabled = disabled || isSubmitting

  return (
    <FormControl className={className} error={hasError}>
      <StyledFormControlLabel
        $isLabelHighlighted={isLabelHighlighted}
        {...field}
        checked={field.value}
        control={<StyledCheckbox color="primary" />}
        disabled={isFieldDisabled}
        gap={16}
        htmlFor={name}
        label={label}
      />
      {hasError && (
        <FormHelperText>
          <>{error?.message}</>
        </FormHelperText>
      )}
    </FormControl>
  )
}
